import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { MicrophoneIcon, StopIcon } from '@heroicons/react/24/solid';
import { Visualizer } from 'react-sound-visualizer';
import { useAuth } from '../../../auth/AuthContext';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../config/colors';
import { apiClient } from '../../../services/apiClient';

const DeepgramRecorder = forwardRef(({ onTranscriptionUpdate, onError, onReset, onStop, layout = "vertical", helperText, additionalHelperText, priority = 'normal' }, ref) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [audioStream, setAudioStream] = useState(null);
  
  const websocket = useRef(null);
  const audioContext = useRef(null);
  const mediaStreamSource = useRef(null);
  const processor = useRef(null);

  // Convert Float32Array to Int16Array with dynamic amplification
  const convertFloat32ToInt16 = useCallback((float32Array) => {
    const int16Array = new Int16Array(float32Array.length);
    
    // Calculate RMS to determine signal strength
    const rms = Math.sqrt(float32Array.reduce((sum, x) => sum + x * x, 0) / float32Array.length);
    
    // Dynamic amplification: stronger when signal is weak, weaker when signal is strong
    // Target RMS of about 0.2 (experimentally determined good value)
    const targetRms = 0.2;
    const AMPLIFICATION = rms > 0.0001 ? targetRms / rms : 50;
    
    // Apply amplification with soft clipping for smoother sound
    for (let i = 0; i < float32Array.length; i++) {
      // Amplify
      const amplified = float32Array[i] * AMPLIFICATION;
      // Soft clip to avoid harsh digital clipping
      const softClipped = Math.sign(amplified) * (1 - Math.exp(-Math.abs(amplified)));
      // Convert to 16-bit integer (-32768 to 32767)
      int16Array[i] = Math.round(softClipped * 32767);
    }
    
    return int16Array;
  }, []);

  const cleanupAudioResources = useCallback(() => {
    if (processor.current) {
      processor.current.disconnect();
      processor.current = null;
    }
    if (audioContext.current) {
      audioContext.current.close();
      audioContext.current = null;
    }
    if (mediaStreamSource.current) {
      mediaStreamSource.current.getTracks().forEach(track => track.stop());
      mediaStreamSource.current = null;
    }
    setAudioStream(null);
  }, []);

  const createAudioProcessor = useCallback(async () => {
    try {
      // Request microphone access with explicit settings
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          sampleSize: 16,
          echoCancellation: true,
          noiseSuppression: true
        }
      });

      mediaStreamSource.current = stream;
      // Set the audio stream for the visualizer
      setAudioStream(stream);

      // Create and initialize audio context
      audioContext.current = new AudioContext({
        sampleRate: 16000,
        latencyHint: 'interactive'
      });

      // Create source from microphone stream
      const source = audioContext.current.createMediaStreamSource(stream);

      // Create audio processor
      const bufferSize = 4096;
      const processor = audioContext.current.createScriptProcessor(bufferSize, 1, 1);

      // Connect the nodes
      source.connect(processor);
      processor.connect(audioContext.current.destination);

      // Set up audio processing
      processor.onaudioprocess = (e) => {
        if (websocket.current?.readyState === WebSocket.OPEN) {
          const inputData = e.inputBuffer.getChannelData(0);
          const audioData = convertFloat32ToInt16(inputData);
          websocket.current.send(audioData.buffer);
        }
      };

      processor.current = processor;
      return processor;
    } catch (error) {
      console.error('Error creating audio processor:', error);
      throw error;
    }
  }, [convertFloat32ToInt16]);

  const stopRecording = useCallback(() => {
    if (websocket.current) {
      websocket.current.send(JSON.stringify({ type: 'stop' }));
      websocket.current.close();
      websocket.current = null;
    }
    
    setIsRecording(false);
    cleanupAudioResources();
    onStop?.();
  }, [cleanupAudioResources, onStop]);

  const startRecording = useCallback(async () => {
    try {
      // Create WebSocket connection using apiClient's base URL
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsBaseUrl = apiClient.defaults.baseURL
        ? apiClient.defaults.baseURL.replace(/^https?:/, wsProtocol)
        : `${wsProtocol}//${window.location.host}`;
      
      websocket.current = new WebSocket(`${wsBaseUrl}/api/transcription/ws`);
      
      websocket.current.onopen = async () => {
        console.log('WebSocket connected, language:', user?.preferences?.language);
        // Send control message to start transcription with selected language
        websocket.current.send(JSON.stringify({
          type: 'start',
          language: user?.preferences?.language || 'en',
          // Add authorization token if available
          token: apiClient.defaults.headers.common['Authorization']?.replace('Bearer ', '')
        }));
        
        // Create audio processor after WebSocket is connected
        await createAudioProcessor();
      };

      websocket.current.onclose = () => {
        cleanupAudioResources();
      };

      websocket.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        onError?.(t('audioRecorder.errors.connection'));
      };

      websocket.current.onmessage = (event) => {
        try {
          console.log(event.data);
          const data = JSON.parse(event.data);

          if (data.type === 'Results') {
            const transcript = data.channel?.alternatives?.[0]?.transcript;
            if (transcript) {
              //TODO To adapt to send an array of transcripts
              onTranscriptionUpdate?.(transcript);
            }
          } else if (data.error) {
            console.error('Received error:', data.error);
            onError?.(data.error);
          }
        } catch (error) {
          console.error('Error handling WebSocket message:', error);
        }
      };

      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      onError?.(t('audioRecorder.errors.permissions'));
    }
  }, [cleanupAudioResources, createAudioProcessor, onError, t, user?.preferences?.language, onTranscriptionUpdate]);

  const handleToggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, stopRecording, startRecording]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'x') {
        // Check if we're inside a modal (higher priority context)
        const isInModal = document.querySelector('[role="dialog"]') !== null;
        const isInFeedbackModal = document.querySelector('[data-feedback-modal]') !== null;
        
        // If this recorder has 'high' priority (feedback modal) or if no modal is open
        const shouldHandle = priority === 'high' || (!isInModal && priority === 'normal');
        
        // Additional check: if we're in a feedback modal, only high priority recorders should respond
        if (isInFeedbackModal && priority !== 'high') {
          console.log(`Deepgram Recorder (priority: ${priority}) - Skipping, feedback modal is open`);
          return;
        }
        
        if (shouldHandle) {
          console.log(`Deepgram Recorder (priority: ${priority}) - Handling shortcut`);
          event.preventDefault();
          event.stopPropagation();
          handleToggleRecording();
        } else {
          console.log(`Deepgram Recorder (priority: ${priority}) - Not handling, modal is open`);
        }
      }
    };

    // Use capture phase for high priority recorders to intercept first
    const useCapture = priority === 'high';
    window.addEventListener('keydown', handleKeyPress, useCapture);
    return () => {
      window.removeEventListener('keydown', handleKeyPress, useCapture);
    };
  }, [handleToggleRecording, priority]);

  useEffect(() => {
    if (onReset) {
      return () => {
        if (isRecording) {
          stopRecording();
        }
        cleanupAudioResources();
      };
    }
  }, [onReset, isRecording, stopRecording, cleanupAudioResources]);

  useImperativeHandle(ref, () => ({
    stopRecording
  }));

  return (
    <div className="px-6 py-4">
      {/* Main Horizontal Controls */}
      <div className="flex items-center justify-between space-x-6 mb-4">
        {/* Left side - Audio Visualizer */}
        <div className="flex-1">
          {isRecording && audioStream ? (
            <Visualizer
              audio={audioStream}
              autoStart={true}
              strokeColor="#c5d6f8"
              mode="continuous" 
              rectWidth={3} 
              slices={50}
              heightNorm={1}
            >
              {({ canvasRef }) => (
                <canvas 
                  ref={canvasRef} 
                  style={{ height: '80px', width: '100%' }}
                  className="rounded-lg"
                />
              )}
            </Visualizer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[80px] bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-sm text-gray-500">
                {isRecording ? t('audioRecorder.recording') : helperText || t('audioRecorder.clickToStart')}
                <span className="ml-2 text-xs text-gray-400">
                  {t('audioRecorder.shortcut')}
                </span>
              </p>
              {!isRecording && additionalHelperText && (
                <p className="text-xs text-gray-400 italic mt-1 px-4 text-center">
                  {additionalHelperText}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Right side - Control Button */}
        <div className="flex items-center">
          {/* Record/Stop Button */}
          <button
            onClick={handleToggleRecording}
            className={`flex items-center justify-center w-16 h-16 rounded-full transition-all duration-200 focus:outline-none focus:ring-4 ${
              isRecording 
                ? 'bg-red-300 hover:bg-red-400 focus:ring-red-200' 
                : 'hover:bg-teal-400 focus:ring-teal-200'
            }`}
            style={!isRecording ? {
              backgroundColor: colors.primary.selected
            } : undefined}
            aria-label={isRecording ? t('audioRecorder.stopRecording') : t('audioRecorder.startRecording')}
          >
            {isRecording ? (
              <StopIcon className="w-8 h-8 text-white" />
            ) : (
              <MicrophoneIcon className="w-8 h-8 text-white" />
            )}
          </button>
        </div>
      </div>

      {/* Recording Status Bar */}
      {isRecording && (
        <div className="flex items-center justify-center space-x-2 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
            <span className="font-medium text-red-600">{t('audioRecorder.recordingStatus')}</span>
          </div>
        </div>
      )}
    </div>
  );
});

export default DeepgramRecorder; 